import CoreEnums from '../../../../core/utils/enums'

/**
 * Field definitions for clinic forms
 * Contains all the form field configurations used throughout the clinics module
 */
export const CLINIC_FIELDS = {
  // Status fields
  termsConditions: {
    label: 'Terms and Conditions:',
    key: 'termsConditions',
    required: false
  },
  afterHourFee: {
    label: 'After Hour Fee:',
    key: 'afterHourFee',
    required: false
  },
  isActive: {
    label: 'Status:',
    key: 'isActive',
    required: false
  },
  geolocation: {
    key: 'geolocation'
  },
  clinicBookings: {
    label: 'Allow Appointments?',
    key: 'clinicBookings',
    required: false
  },
  homeVisits: {
    label: 'Allow Home Visits?',
    key: 'homeVisits',
    required: false
  },
  healthTele: {
    label: 'Allow Telehealth Services?',
    key: 'healthTele',
    required: false
  },
  vitalsMonitoring: {
    label: 'Offer Vitals Monitoring?',
    key: 'vitalsMonitoring',
    required: false
  },
  walkinsAllowed: {
    label: 'Allow Walk-ins?',
    key: 'walkinsAllowed',
    required: false
  },

  // Basic information fields
  name: {
    label: 'Provide the facility`s name:',
    placeholder: 'e.g. Meta-Clinic Mill Park',
    key: 'name',
    required: true,
    validationMsg:
      'Please enter your facility name. This will be displayed to patients when booking appointments and on all official documentation.'
  },
  clinicType: {
    label: 'Facility Type:',
    placeholder: '',
    key: 'clinicType',
    required: false,
    validationMsg:
      'Please select the type of facility you operate. This helps categorize your services and ensures appropriate workflows are applied.'
  },
  paymentMethods: {
    label: 'Accepted Payment Methods:',
    placeholder: '',
    key: 'paymentMethods',
    options: [
      {
        value: 'Cash',
        label: 'Cash'
      },
      { value: 'Card', label: 'Card' },
      {
        value: 'Medical Aid',
        label: 'Medical Aid'
      }
    ],
    required: true,
    validationMsg:
      'Please select at least one payment method. Patients need to know how they can pay for their services before booking appointments.'
  },

  // Location fields
  province: {
    label: 'Provide the province this facility is situated in:',
    key: 'province',
    required: true,
    validationMsg:
      'Please select the province where your facility is located. This is required for regulatory compliance and helps patients find your services in their area.',
    options: Object.keys(CoreEnums.provinces).map((key) => {
      return {
        label: CoreEnums.provinces[key],
        value: CoreEnums.provinces[key]
      }
    })
  },
  address: {
    label: 'Search for Facility:',
    placeholder: 'e.g Meta-Clinic Cape Road, Mill Park',
    key: 'address',
    required: true,
    validationMsg:
      'Please provide your facility address using the search function. This ensures accurate location data for GPS navigation and emergency services.'
  },
  physicalAddress: {
    label: 'Facility Address:',
    placeholder: '',
    key: 'physicalAddress',
    required: false,
    validationMsg: ''
  },
  place_id: {
    key: 'place_id'
  },
  phoneNumber: {
    label: 'Phone Number:',
    placeholder: 'e.g. 011555****',
    required: true,
    key: 'phoneNumber',
    validationMsg:
      'Please enter a valid contact phone number. Patients and staff need this for appointments, emergencies, and general inquiries.'
  },
  email: {
    label: 'Email:',
    placeholder: 'e.g. clinic@domain.co.za',
    required: true,
    key: 'email',
    validationMsg:
      'Please enter a valid email address. This will be used for official communications, appointment confirmations, and system notifications.'
  },

  // Practice details fields
  practiceName: {
    label: 'Practice Name:',
    placeholder: 'e.g. Dr Jane Doe Medical',
    required: true,
    key: 'practiceName',
    validationMsg:
      'Please enter the official practice name as registered with medical authorities. This must match your registration documents for billing and compliance purposes.'
  },
  practiceRegNumber: {
    label: 'Practice Registration Number:',
    placeholder: 'e.g. 2778 / 334 / 440',
    required: true,
    key: 'practiceRegNumber',
    validationMsg:
      'Please enter your practice registration number exactly as issued by the medical council. This is required for legal compliance and medical aid billing.'
  },
  practiceNumber: {
    label: 'Practice Number:',
    placeholder: 'e.g. 278837741',
    required: true,
    key: 'practiceNumber',
    validationMsg:
      'Please enter your unique practice number. This identifier is used for billing, insurance claims, and official healthcare system integrations.'
  },
  bpmKey: {
    label: 'Select the Business Process for this facility:',
    key: 'bpmKey',
    required: true,
    validationMsg:
      'Please select a business process workflow. This determines how patients flow through your facility and affects appointment scheduling, staff workflows, and billing procedures.'
  },
  bpmConfig: {
    key: 'bpmConfig',
    required: false
  },

  // CGM Integration fields - Disabled (not currently used)
  // cgmEnabled: {
  //   label: 'CGM Enabled:',
  //   key: 'cgmEnabled',
  //   required: false
  // },
  // cgmPracticeId: {
  //   label: 'CGM Practice ID:',
  //   placeholder: 'e.g. 278837741',
  //   required: true,
  //   key: 'cgmPracticeId'
  // },

  // Billing Profile fields
  billingPracticeName: {
    label: 'Practice Name:',
    placeholder: 'Enter practice name',
    key: 'billingPracticeName',
    required: true,
    validationMsg:
      'Please enter the practice name for billing purposes. This name will appear on all invoices and insurance claims submitted for this facility.'
  },
  disciplineType: {
    label: 'Discipline Type:',
    key: 'disciplineType',
    required: true,
    validationMsg:
      'Please select your medical discipline type. This determines the billing codes and rates that will be applied to your services and affects insurance reimbursements.'
  },
  disciplinePrefix: {
    label: 'Discipline Prefix:',
    key: 'disciplinePrefix',
    required: false
  },
  roomsDefault: {
    label: 'Practice Rate - Rooms: Default Base Rate or %:',
    placeholder: 'Enter default base rate or percentage',
    key: 'roomsDefault',
    required: false
  },
  network: {
    label: 'Network:',
    key: 'network',
    required: false
  },
  ipa: {
    label: 'IPA:',
    key: 'ipa',
    required: false
  },
  dispensing: {
    label: 'Dispensing:',
    key: 'dispensing',
    required: false
  },
  schemeContracts: {
    label: 'Contracted In Schemes:',
    key: 'schemeContracts',
    required: true,
    validationMsg:
      'Please select at least one medical aid scheme that you have contracts with. This ensures proper billing rates and coverage verification for your patients.'
  },

  // Services Offered fields
  servicesOffered: {
    label: 'Services Offered:',
    key: 'servicesOffered',
    required: false,
    validationMsg:
      'Please select at least one service that your facility offers. Patients need to know what services are available when booking appointments.'
  }
}
